import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, MapPin, Plus, Trash2, CreditCard, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const BulkLocationCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([
    {
      id: Date.now(),
      name: '',
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      address: ''
    }
  ]);

  const addLocation = () => {
    setLocations([...locations, {
      id: Date.now(),
      name: '',
      street: '',
      house_number: '',
      postal_code: '',
      city: '',
      address: ''
    }]);
  };

  const removeLocation = (id) => {
    if (locations.length === 1) {
      toast.error('Mindestens ein Standort ist erforderlich');
      return;
    }
    setLocations(locations.filter(loc => loc.id !== id));
  };

  const updateLocation = (id, field, value) => {
    setLocations(locations.map(loc => 
      loc.id === id ? { ...loc, [field]: value } : loc
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    for (let i = 0; i < locations.length; i++) {
      const loc = locations[i];
      if (!loc.name.trim()) {
        toast.error(`Standort ${i + 1}: Name ist erforderlich`);
        return;
      }
      if (!loc.city.trim() && !loc.address.trim()) {
        toast.error(`Standort ${i + 1}: Stadt oder Adresse ist erforderlich`);
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const createdLocationIds = [];
      
      // Create all locations
      for (const location of locations) {
        const response = await axios.post(
          `${API}/api/admin/locations`,
          location,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const newLocation = response.data;
        if (newLocation.payment_required) {
          createdLocationIds.push(newLocation.id);
        }
      }
      
      toast.success(`${locations.length} Standorte erfolgreich erstellt!`);
      
      if (createdLocationIds.length === 0) {
        // All locations are free (should not happen for additional locations)
        navigate('/admin/locations');
        return;
      }
      
      if (createdLocationIds.length === 1) {
        // Single location - redirect to single payment page
        navigate(`/admin/locations/${createdLocationIds[0]}/payment`);
      } else {
        // Multiple locations - redirect to bulk payment page
        navigate('/admin/locations/bulk-payment', {
          state: { locationIds: createdLocationIds }
        });
      }
      
    } catch (error) {
      console.error('Error creating locations:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen der Standorte');
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = locations.length * 14.99;
  const totalVat = totalPrice * 0.19;
  const totalGross = totalPrice + totalVat;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="space-y-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/locations')}
            className="mb-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Zurück zu Standorten
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MapPin className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Mehrere Standorte hinzufügen</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Fügen Sie mehrere Standorte hinzu und bezahlen Sie zusammen
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-start gap-3">
            <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 dark:text-blue-100">
              <p className="font-semibold mb-1">Bulk-Rabatt verfügbar</p>
              <p>
                Fügen Sie mehrere Standorte hinzu und bezahlen Sie alle zusammen. 
                Preis: 14,99€/Monat pro Standort.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Location Forms */}
          {locations.map((location, index) => (
            <Card key={location.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Badge variant="outline">{index + 1}.</Badge>
                    Standort {index + 1}
                  </CardTitle>
                  {locations.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLocation(location.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Standortname *</label>
                  <input
                    type="text"
                    value={location.name}
                    onChange={(e) => updateLocation(location.id, 'name', e.target.value)}
                    placeholder="z.B. JET Winnenden"
                    className="mt-1 w-full px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Straße</label>
                    <input
                      type="text"
                      value={location.street}
                      onChange={(e) => updateLocation(location.id, 'street', e.target.value)}
                      placeholder="Beispielstraße"
                      className="mt-1 w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Hausnummer</label>
                    <input
                      type="text"
                      value={location.house_number}
                      onChange={(e) => updateLocation(location.id, 'house_number', e.target.value)}
                      placeholder="1"
                      className="mt-1 w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">PLZ</label>
                    <input
                      type="text"
                      value={location.postal_code}
                      onChange={(e) => updateLocation(location.id, 'postal_code', e.target.value)}
                      placeholder="12345"
                      className="mt-1 w-full px-3 py-2 border rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Stadt *</label>
                    <input
                      type="text"
                      value={location.city}
                      onChange={(e) => updateLocation(location.id, 'city', e.target.value)}
                      placeholder="Berlin"
                      className="mt-1 w-full px-3 py-2 border rounded-md"
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add Location Button */}
          <Button
            type="button"
            variant="outline"
            onClick={addLocation}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Weiteren Standort hinzufügen
          </Button>

          {/* Price Summary */}
          <Card className="border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/20">
            <CardContent className="p-4 sm:p-6">
              <h3 className="font-semibold mb-3 text-green-900 dark:text-green-100">
                💶 Preisübersicht
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-800 dark:text-green-200">
                    {locations.length} × Standort (14,99€/Monat)
                  </span>
                  <span className="font-medium text-green-900 dark:text-green-100">
                    {totalPrice.toFixed(2)}€
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-800 dark:text-green-200">MwSt. (19%)</span>
                  <span className="font-medium text-green-900 dark:text-green-100">
                    {totalVat.toFixed(2)}€
                  </span>
                </div>
                <div className="h-px bg-green-300 dark:bg-green-700 my-2"></div>
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-green-900 dark:text-green-100">Gesamt</span>
                  <span className="text-green-600 dark:text-green-400">
                    {totalGross.toFixed(2)}€/Monat
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Erstelle Standorte...
              </>
            ) : (
              <>
                <CreditCard className="h-5 w-5 mr-2" />
                Standorte erstellen & bezahlen
              </>
            )}
          </Button>
        </form>
      </div>
    </DashboardLayout>
  );
};
